"use client";

import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Editor } from "@tinymce/tinymce-react";
import MultiSelect from "@/components/ui/multi-select";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { TagInput } from "@/components/ui/tag-input";
import { TinyMCEImagePicker } from "@/components/ui/tinymce-image-picker";
import { imageUploadPlugin } from "@/components/ui/tinymce-upload-plugin";
import { JsonLdBuilder } from "@/components/ui/json-ld-builder";
import { SeoPreview } from "@/components/ui/seo-preview";
import { usePageStore } from "@/stores/pageStore";
import { useAuthorStore } from "@/stores/authorStore";
import { CreatePageDto } from "@/types/page";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { ArrowLeft, Save, X, Eye } from "lucide-react";

// Validation schema
const createPageSchema = z.object({
  title: z
    .string()
    .min(1, "Tiêu đề không được để trống")
    .max(255, "Tiêu đề không được quá 255 ký tự"),
  content: z.string().min(1, "Nội dung không được để trống"),
  slug: z
    .string()
    .min(1, "Đường dẫn không được để trống")
    .max(255, "Đường dẫn không được quá 255 ký tự"),
  author: z.string().min(1, "Tác giả không được để trống"),
  pageTitle: z.string().min(1, "Tiêu đề trang là bắt buộc"),
  pageDescription: z.string().min(1, "Mô tả trang là bắt buộc"),
  canonicalUrl: z.string().min(1, "Canonical URL là bắt buộc"),
  keywords: z.array(z.string()).optional(),
  visibility: z.enum(["public", "private"]).optional(),
  publishDate: z.string().min(1, "Ngày xuất bản là bắt buộc"),
  updateDate: z.string().optional(),
  isDisplay: z.boolean().optional(),
  // Các trường mới
  mainKeyword: z.string().min(1, "Keyword chính là bắt buộc"),
  keywordUrl: z.string().optional(),
  schemaJsonLd: z.string().optional(),
});

type CreatePageFormData = z.infer<typeof createPageSchema>;

export default function CreatePagePage() {
  const navigate = useNavigate();
  const { createPage } = usePageStore();

  const { authors, fetchAuthors } = useAuthorStore();
  const editorRef: any = useRef(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // TinyMCE Image Picker states
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreatePageFormData>({
    resolver: zodResolver(createPageSchema),
    defaultValues: {
      title: "",
      content: "",
      slug: "",
      author: "",

      pageTitle: "",
      pageDescription: "",
      canonicalUrl: "",
      keywords: [],

      mainKeyword: "",
      visibility: "public",
      isDisplay: true,
      publishDate: "",
      updateDate: "",
      schemaJsonLd: "",
    },
  });

  // Auto-generate slug from main keyword (dùng biến watch ổn định để tránh loop)
  const mainKeywordValue = watch("mainKeyword");
  useEffect(() => {
    const mainKeyword = mainKeywordValue;
    if (mainKeyword && mainKeyword.trim()) {
      const slug = mainKeyword
        .toLowerCase()
        // Convert Vietnamese characters to Latin
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
        .replace(/đ/g, "d") // Convert đ to d
        .replace(/Đ/g, "d") // Convert Đ to d
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-") // Replace spaces with hyphens
        .replace(/-+/g, "-") // Replace multiple hyphens with single
        .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
        .trim();

      setValue("slug", slug);
      setValue("canonicalUrl", `${slug}`);
    }
  }, [mainKeywordValue, setValue]);

  // Fetch external data when component mounts
  useEffect(() => {
    fetchAuthors({ page: 1, limit: 1000 });
  }, [fetchAuthors]);

  const onSubmit = async (data: CreatePageFormData) => {
    setIsSubmitting(true);
    try {
      const _data: any = {};
      _data.title = data.title;
      _data.content = data.content;
      _data.slug = data.slug;
      _data.author = data.author;

      if (_data.pageTitle) data.pageTitle = data.pageTitle;
      if (_data.pageDescription) data.pageDescription = data.pageDescription;
      if (_data.canonicalUrl) data.canonicalUrl = data.canonicalUrl;
      data.keywords = data.keywords;

      if (_data.visibility) data.visibility = data.visibility;
      if (_data.publishDate) data.publishDate = data.publishDate;
      if (data.updateDate) _data.updateDate = data.updateDate;
      if (data.schemaJsonLd) _data.schemaJsonLd = data.schemaJsonLd;
      // isDisplay là optional; không append để tránh lỗi boolean từ backend
      if (_data.mainKeyword) data.mainKeyword = data.mainKeyword;
      if (_data.keywordUrl) data.keywordUrl = data.keywordUrl;

      await createPage(data as unknown as CreatePageDto);
      navigate("/pages");
    } catch (error) {
      console.error("Error creating page:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/pages");
  };

  // TinyMCE Image Picker handlers
  const handleOpenImagePicker = useCallback(() => {
    setIsImagePickerOpen(true);
  }, []);

  const handleImageSelect = useCallback(
    (payload: {
      url: string;
      alt?: string;
      title?: string;
      caption?: string;
      linkImage?: string;
      width?: number;
      height?: number;
      rel?: string;
      target?: string;
      id?: string;
    }) => {
      if (editorRef.current) {
        const imgTag = `<img src="${payload.url}" ${
          payload.id ? `data-image-id="${payload.id}"` : ""
        } alt="${payload.alt || ""}" ${
          payload.width ? `width="${payload.width}"` : ""
        } ${payload.height ? `height="${payload.height}"` : ""} ${
          payload.title ? `title="${payload.title}"` : ""
        } style="max-width: 100%; height: auto;" />`;
        const html = payload.linkImage
          ? `<a href="${payload.linkImage}" ${
              payload.target ? `target="${payload.target}"` : ""
            } ${payload.rel ? `rel="${payload.rel}"` : ""}>${imgTag}</a>`
          : imgTag;
        editorRef.current.insertContent(html);
      }
      setIsImagePickerOpen(false);
    },
    []
  );

  // Preview handler
  const handlePreview = () => {
    // TODO: Implement preview functionality
    console.log("Preview clicked");
  };

  // Memoize Editor to avoid re-render when other fields change
  const contentValue = watch("content");
  const memoizedEditor = useMemo(
    () => (
      <Editor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey="gpl"
        onInit={(_evt, editor) => (editorRef.current = editor)}
        value={contentValue || ""}
        onEditorChange={(content) => setValue("content", content)}
        init={{
          height: 400,
          menubar: false,
          plugins: [
            "code",
            "advlist",
            "anchor",
            "autolink",
            "charmap",
            "fullscreen",
            "help",
            "image",
            "insertdatetime",
            "link",
            "lists",
            "media",
            "preview",
            "searchreplace",
            "table",
            "visualblocks",
          ],
          toolbar:
            "code | undo redo | styles | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | customimage",
          content_style:
            "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
          link_rel_list: [
            { title: "Nothing", value: "" },
            {
              title: "No Referrer",
              value: "noreferrer",
            },
            { title: "No Follow", value: "nofollow" },
            { title: "Sponsored", value: "sponsored" },
            { title: "UGC", value: "ugc" },
          ],
          z_index: 99999,
          z_index_base: 99999,
          dialog_type: "modal",
          body_class: "tinymce-dialog-override",
          dialog_prefix: "tox-",
          // Enable paste and drop for images
          paste_data_images: true,
          automatic_uploads: true,
          file_picker_types: "image",
          setup: (editor) => {
            editor.on("init", () => {
              // Add custom image button
              editor.ui.registry.addButton("customimage", {
                text: "Hình ảnh",
                icon: "image",
                onAction: () => {
                  handleOpenImagePicker();
                },
              });

              // Setup image upload plugin
              imageUploadPlugin(editor);
            });
            editor.on("OpenDialog", (_e) => {
              setTimeout(() => {
                const dialogs = document.querySelectorAll(".tox-dialog");
                dialogs.forEach((dialog) => {
                  (dialog as HTMLElement).style.zIndex = "99999";
                });
              }, 100);
            });
          },
        }}
      />
    ),
    [contentValue, setValue, handleOpenImagePicker]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Quay lại
            </Button>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Tạo trang mới
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePreview}
              className="flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Xem trước
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Hủy
            </Button>
            <Button
              type="submit"
              form="create-page-form"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4" />
              {isSubmitting ? "Đang lưu..." : "Lưu trang"}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-12 p-6">
        <form
          id="create-page-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Content Section */}
              <div className="bg-white rounded-lg p-6">
                <Label
                  htmlFor="content"
                  className="text-lg font-semibold mb-3 block"
                >
                  Nội dung
                </Label>
                {memoizedEditor}
                {errors.content && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.content.message}
                  </p>
                )}
              </div>

              {/* Author and Category Section */}
              <div className="bg-white rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="author">Tên tác giả</Label>
                    <MultiSelect
                      options={authors.map((a) => ({
                        label: a.name,
                        value: a._id,
                      }))}
                      value={(watch("author") || "")
                        .split(",")
                        .map((s: string) => s.trim())
                        .filter(Boolean)}
                      onChange={(vals) => setValue("author", vals.join(", "))}
                      placeholder="Người viết"
                    />
                    {errors.author && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.author.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* SEO Section */}
              <div className="bg-white rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Tối ưu SEO</h3>
                  <button
                    type="button"
                    className="text-blue-600 text-sm hover:underline"
                  >
                    Chỉnh sửa SEO
                  </button>
                </div>

                <div className="mb-4 text-sm text-gray-600">
                  Thiết lập các thẻ mô tả giúp khách hàng dễ dàng tìm thấy danh
                  mục này trên công cụ tìm kiếm như Google
                </div>

                <div className="mb-4 text-sm text-blue-600">
                  https://example.com/blogs/news/trang-mau
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Tiêu đề (Thẻ H1)</Label>
                    <Input
                      id="title"
                      {...register("title")}
                      placeholder="Trang mẫu"
                    />
                    {errors.title && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.title.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageTitle">
                        Tiêu đề trang (đề xuất từ 40 - 60 ký tự)
                      </Label>
                      <span className="text-xs text-gray-500">
                        {watch("pageTitle")?.length || 0} / 70 ký tự
                      </span>
                    </div>
                    <Input
                      id="pageTitle"
                      {...register("pageTitle")}
                      placeholder="Trang mẫu"
                      maxLength={70}
                    />
                    {errors.pageTitle && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.pageTitle.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="pageDescription">
                        Mô tả trang (đề xuất từ 50 - 155 ký tự)
                      </Label>
                      <span className="text-xs text-gray-500">
                        {watch("pageDescription")?.length || 0} / 320 ký tự
                      </span>
                    </div>
                    <Textarea
                      id="pageDescription"
                      {...register("pageDescription")}
                      placeholder="Mô tả ngắn gọn về trang cho SEO"
                      maxLength={320}
                      rows={3}
                    />
                    {errors.pageDescription && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.pageDescription.message}
                      </p>
                    )}
                  </div>

                  {/* SEO & URL Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      SEO & URL
                    </h3>

                    <div>
                      <Label htmlFor="mainKeyword">Keyword chính</Label>
                      <Input
                        id="mainKeyword"
                        {...register("mainKeyword")}
                        placeholder="Nhập keyword chính..."
                      />
                      {errors.mainKeyword && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.mainKeyword.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="slug">Đường dẫn</Label>
                      <Input
                        id="slug"
                        {...register("slug")}
                        placeholder="trang-mau"
                      />
                      {errors.slug && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.slug.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <Label htmlFor="canonicalUrl">Canonical URL</Label>
                        <span className="text-xs text-gray-500">
                          {watch("canonicalUrl")?.length || 0} / 255 ký tự
                        </span>
                      </div>
                      <Input
                        id="canonicalUrl"
                        {...register("canonicalUrl")}
                        placeholder="https://example.com/blogs/news/trang-mau"
                        maxLength={255}
                      />
                      {errors.canonicalUrl && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.canonicalUrl.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="keywords">Keywords</Label>
                      <TagInput
                        value={watch("keywords") || []}
                        onChange={(tags) => setValue("keywords", tags)}
                        placeholder="Nhập từ khóa và nhấn Enter để thêm..."
                        maxTags={10}
                      />
                      {errors.keywords && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.keywords.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label htmlFor="schemaJsonLd">JSON-LD Schema</Label>
                        <JsonLdBuilder
                          formData={watch()}
                          availableFields={[
                            { key: "title", label: "Tiêu đề (H1)" },
                            { key: "pageTitle", label: "Tiêu đề trang (SEO)" },
                            { key: "pageDescription", label: "Mô tả trang" },
                            { key: "canonicalUrl", label: "Canonical URL" },
                            { key: "mainKeyword", label: "Keyword chính" },
                            { key: "slug", label: "Đường dẫn (Slug)" },
                            {
                              key: "keywords",
                              label: "Keywords",
                              getValue: (data) =>
                                Array.isArray(data.keywords)
                                  ? data.keywords.join(", ")
                                  : "",
                            },
                            {
                              key: "authorName",
                              label: "Tên tác giả",
                              getValue: (data) => {
                                const authorId = data.author;
                                const author = authors.find(
                                  (a) => a._id === authorId
                                );
                                return author?.name || "";
                              },
                            },
                            {
                              key: "publishDate",
                              label: "Ngày xuất bản",
                              getValue: (data) =>
                                data.publishDate || new Date().toISOString(),
                            },
                            {
                              key: "updatedDate",
                              label: "Ngày cập nhật",
                              getValue: () => new Date().toISOString(),
                            },
                            {
                              key: "content",
                              label: "Nội dung (Text only)",
                              getValue: (data) => {
                                const div = document.createElement("div");
                                div.innerHTML = data.content || "";
                                return div.textContent || div.innerText || "";
                              },
                            },
                          ]}
                          value={watch("schemaJsonLd") || ""}
                          onChange={(value) => setValue("schemaJsonLd", value)}
                          pageType="page"
                        />
                      </div>
                      <div className="mb-2">
                        <p className="text-xs text-gray-500">
                          Nhập JSON-LD structured data cho SEO hoặc dùng nút
                          "Tạo từ Template"
                        </p>
                      </div>
                      <Textarea
                        id="schemaJsonLd"
                        {...register("schemaJsonLd")}
                        placeholder={`{
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Tiêu đề trang",
  "description": "Mô tả trang"
}`}
                        rows={10}
                        className="font-mono text-xs"
                      />
                      {watch("schemaJsonLd") &&
                        (() => {
                          try {
                            JSON.parse(watch("schemaJsonLd")!);
                            return (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ JSON hợp lệ
                              </p>
                            );
                          } catch {
                            return (
                              <p className="text-xs text-red-600 mt-1">
                                ✗ JSON không hợp lệ
                              </p>
                            );
                          }
                        })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* SEO Preview */}
              <SeoPreview
                pageTitle={watch("pageTitle") || watch("title") || ""}
                pageDescription={watch("pageDescription") || ""}
                url={
                  watch("canonicalUrl") ||
                  `https://phimmoihd.it.com/${watch("slug") || ""}`
                }
                siteName="GhienPhim"
              />

              {/* Visibility Section */}
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-4">Hiển thị</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="visibility">Chế độ hiển thị</Label>
                    <Select
                      value={watch("visibility") || "public"}
                      onValueChange={(v) => setValue("visibility", v as any)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn chế độ" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Công khai</SelectItem>
                        <SelectItem value="private">Riêng tư</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Date Section */}
              <div className="bg-white rounded-lg p-6">
                <h3 className="font-semibold mb-4">Ngày tháng</h3>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="publishDate">Ngày xuất bản</Label>
                    <DateTimePicker
                      date={
                        watch("publishDate")
                          ? new Date(watch("publishDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("publishDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày xuất bản"
                    />
                    {errors.publishDate && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.publishDate.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="updateDate">Ngày cập nhật</Label>
                    <DateTimePicker
                      date={
                        watch("updateDate")
                          ? new Date(watch("updateDate")!)
                          : undefined
                      }
                      onChange={(date) =>
                        setValue("updateDate", date?.toISOString() || "")
                      }
                      placeholder="Chọn ngày cập nhật"
                    />
                  </div>

                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                    <p>
                      <strong>Ngày tạo:</strong>{" "}
                      {new Date().toLocaleString("vi-VN")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* TinyMCE Image Picker */}
        <TinyMCEImagePicker
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      </div>
    </div>
  );
}
